import { computed, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/sync/auth'
import { getSupabase } from '@/sync/supabase'
import { createSyncedDoc, type SyncedDoc } from '@/sync/yjs-sync'
import { connectCollection } from '@/sync/connect'
import * as repo from '@/db/repositories'
import type { FavoriteRow } from '@/db/repositories'

/**
 * Composable que sincroniza los favoritos del usuario vía CRDT.
 *
 * Calco de `useShelvesSync`: un ÚNICO doc global por usuario (room
 * `favorites:${userId}`) con un `Y.Array<FavoriteRow>` (`{ id, addedAt }`,
 * donde `id` = bookId). Los favoritos son cross-device y cross-book, así
 * que conviven durante toda la sesión en un solo doc.
 *
 * Reutiliza `connectCollection` (que exige `{ id: string }`) y
 * `repo.reconcileFavorites` como persistencia local de los cambios
 * remotos. La fuente de verdad es el CRDT; la tabla IndexedDB es caché.
 */

/** Doc activo a nivel de módulo (singleton global de favoritos). */
let current: { synced: SyncedDoc; cleanup: () => void } | null = null

// Flush final al cerrar la pestaña, igual que en useSync/useShelvesSync.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    current?.synced.flushBeacon()
  })
}

export function useFavoritesSync() {
  const settings = useSettingsStore()
  const auth = useAuthStore()
  const { settings: settingsRefs } = storeToRefs(settings)

  const syncEnabled = computed(
    () => settingsRefs.value.sync.enabled && auth.isAuthenticated,
  )

  /**
   * Abre el doc global de favoritos y lo conecta al ref del store.
   * Devuelve true si el sync quedó activo; false si no hay sesión/sync.
   * Idempotente: si ya está abierto, no hace nada.
   */
  function openFavoritesSync(favoritesRef: Ref<FavoriteRow[]>): boolean {
    if (!syncEnabled.value || !auth.userId) return false

    const client = getSupabase(settingsRefs.value.sync)
    if (!client) return false

    // Ya abierto: no reiniciar.
    if (current) return true

    const room = `favorites:${auth.userId}`
    const syncCfg = settingsRefs.value.sync
    const restBase = syncCfg.supabaseUrl ? `${syncCfg.supabaseUrl.replace(/\/$/, '')}/rest/v1` : undefined
    const synced = createSyncedDoc(room, client, auth.userId, restBase, syncCfg.supabaseAnonKey)
    current = { synced, cleanup: () => {} }

    // Persistencia local de cambios remotos (el CRDT es la fuente de verdad).
    connectCollection(favoritesRef, synced, 'favorites', repo.reconcileFavorites)
      .then((result) => {
        if (current) {
          current.cleanup = result.disconnect
        } else {
          // Se cerró mientras conectábamos; limpiar.
          result.disconnect()
        }
      })
      .catch((e) => console.warn('[sync-favorites] conexión falló:', e))

    return true
  }

  /** Cierra el doc de favoritos (persiste y desconecta). */
  function closeFavoritesSync() {
    if (current) {
      current.cleanup()
      current.synced.destroy()
      current = null
    }
  }

  return { syncEnabled, openFavoritesSync, closeFavoritesSync }
}
